import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  UserPlus, Pencil, Trash2, Copy, KeyRound, Check, X, Mail, Shield,
} from "lucide-react";

const inputCls =
  "w-full bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] rounded-[2px] px-3.5 py-2.5 text-sm text-[color:var(--atech-text)] focus:outline-none focus:border-[color:var(--atech-copper)]";

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="label block mb-2">{label}</span>
    {children}
    {hint && <span className="block mt-2 text-[11px] text-[color:var(--atech-text-muted)]">{hint}</span>}
  </label>
);

/* ───────────── Internal users tab ─────────────────────────────────────── */

export function InternalUsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetInfo, setResetInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users", { params: { role: "admin" } });
      setUsers(data);
    } catch {
      setUsers([]);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onDelete = async (u) => {
    if (u.id === me?.id) return; // belt-and-braces — backend also blocks
    if (!confirm(`Delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/users/${u.id}`);
      await load();
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const onResetLink = async (u) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${u.id}/admin-reset-password`);
      setResetInfo(data);
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="internal-users-tab">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="label text-[color:var(--atech-copper)] mb-1">Practice access</div>
          <h2 className="font-display text-2xl text-[color:var(--atech-text)]">Internal users</h2>
          <p className="text-sm text-[color:var(--atech-text-dim)] mt-2 max-w-xl">
            Practice staff who can log in as administrators. Each internal user gets full access to projects, clients, settings, and billing.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="btn-copper text-sm flex items-center gap-2"
          data-testid="users-add-button"
        >
          <UserPlus size={14} /> Add user
        </button>
      </div>

      <div className="card-surface overflow-hidden" data-testid="users-table">
        {loading ? (
          <div className="p-6 label text-[color:var(--atech-text-muted)]">Loading…</div>
        ) : users.length === 0 ? (
          <div className="p-10 text-center">
            <div className="text-sm text-[color:var(--atech-text)]">No internal users yet.</div>
            <p className="text-xs text-[color:var(--atech-text-dim)] mt-1.5">
              Add a user to give a colleague admin access to the portal.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--atech-surface-elevated)] text-[10px] uppercase tracking-[0.18em] text-[color:var(--atech-text-muted)]">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Name</th>
                <th className="text-left px-5 py-3 font-medium">Email</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="text-left px-5 py-3 font-medium">Last sign-in</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--atech-border)]">
              {users.map((u) => {
                const isMe = u.id === me?.id;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-3.5 text-[color:var(--atech-text)]">
                      <span className="inline-flex items-center gap-2">
                        <Shield size={12} className="text-[color:var(--atech-copper)]" />
                        {u.name}
                        {isMe && (
                          <span className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--atech-text-muted)] ml-1">
                            (you)
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-[color:var(--atech-text-dim)] font-mono text-xs">{u.email}</td>
                    <td className="px-5 py-3.5">
                      <StatusChip status={u.status} />
                    </td>
                    <td className="px-5 py-3.5 text-[color:var(--atech-text-dim)] text-xs">
                      {u.last_login_at
                        ? new Date(u.last_login_at).toLocaleString("en-GB", {
                            day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                          })
                        : "Never"}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => onResetLink(u)}
                          disabled={busy}
                          title="Generate password reset link"
                          className="text-xs flex items-center gap-1.5 px-2.5 py-1.5 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:border-[color:var(--atech-copper)] disabled:opacity-50"
                          data-testid={`user-reset-${u.id}`}
                        >
                          <KeyRound size={11} /> Reset link
                        </button>
                        <button
                          onClick={() => setEditing(u)}
                          className="text-xs flex items-center gap-1.5 text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)]"
                          data-testid={`user-edit-${u.id}`}
                        >
                          <Pencil size={11} /> Edit
                        </button>
                        {!isMe && (
                          <button
                            onClick={() => onDelete(u)}
                            disabled={busy}
                            className="text-xs flex items-center gap-1.5 text-[color:var(--atech-text-dim)] hover:text-[#F87171] disabled:opacity-50"
                            data-testid={`user-delete-${u.id}`}
                          >
                            <Trash2 size={11} /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreateInternalUserModal
          onClose={() => setShowCreate(false)}
          onCreated={(created) => { setShowCreate(false); load(); setResetInfo({ ...created, justCreated: true }); }}
        />
      )}
      {editing && (
        <EditInternalUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {resetInfo && (
        <ShareResetLinkModal info={resetInfo} onClose={() => setResetInfo(null)} />
      )}
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    active:        { fg: "var(--st-complete-fg)", bg: "var(--st-complete-bg)", bd: "var(--st-complete-bd)", label: "Active" },
    pending_invite:{ fg: "var(--st-pending-fg)",  bg: "var(--st-pending-bg)",  bd: "var(--st-pending-bd)",  label: "Pending" },
    invited:       { fg: "var(--st-in-design-fg)", bg: "var(--st-in-design-bg)", bd: "var(--st-in-design-bd)", label: "Invited" },
    disabled:      { fg: "var(--st-on-hold-fg)",  bg: "var(--st-on-hold-bg)",  bd: "var(--st-on-hold-bd)",  label: "Disabled" },
  };
  const s = map[status] || map.active;
  return (
    <span
      className="inline-flex items-center px-2 py-1 rounded-[2px] text-[10px] uppercase tracking-[0.16em] border"
      style={{ color: s.fg, backgroundColor: s.bg, borderColor: s.bd }}
    >
      {s.label}
    </span>
  );
}

function CreateInternalUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    if (form.password.length < 8) { setErr("Temp password must be at least 8 characters."); setBusy(false); return; }
    try {
      await api.post("/users", { ...form, role: "admin" });
      onCreated({ user_email: form.email, user_name: form.name, temp_password: form.password });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };
  return (
    <Modal title="Add internal user" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" data-testid="users-create-form">
        <Field label="Full name *">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="users-create-name" />
        </Field>
        <Field label="Email *">
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="users-create-email" />
        </Field>
        <Field
          label="Temporary password *"
          hint="Share this with the user out-of-band. They'll be prompted to change it after first login."
        >
          <input required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} data-testid="users-create-password" />
        </Field>
        {err && <ErrorBlock text={err} />}
        <div className="flex justify-end pt-2">
          <button type="submit" disabled={busy} className="btn-copper text-sm" data-testid="users-create-submit">
            {busy ? "Creating…" : "Create user"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditInternalUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ name: user.name || "", email: user.email || "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.patch(`/users/${user.id}`, form);
      onSaved();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };
  return (
    <Modal title={`Edit ${user.name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" data-testid="users-edit-form">
        <Field label="Full name">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="users-edit-name" />
        </Field>
        <Field label="Email">
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="users-edit-email" />
        </Field>
        {err && <ErrorBlock text={err} />}
        <div className="flex justify-end pt-2">
          <button type="submit" disabled={busy} className="btn-copper text-sm" data-testid="users-edit-submit">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ShareResetLinkModal({ info, onClose }) {
  const [copied, setCopied] = useState("");
  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(key); setTimeout(() => setCopied(""), 1600); });
  };
  return (
    <Modal title={info.justCreated ? "User created" : info.justResent ? "Invite resent" : "Password reset link"} onClose={onClose} testId="users-share-modal">
      <p className="text-sm text-[color:var(--atech-text-dim)] mb-5">
        {info.justCreated
          ? <>Send these credentials to <strong className="text-[color:var(--atech-text)]">{info.user_name}</strong> via Slack, SMS, or another secure channel. They can change the password from <em>Account</em> after signing in.</>
          : info.justResent
            ? <>A fresh activation link has been {info.emailSent ? "emailed to " : "generated for "}<strong className="text-[color:var(--atech-text)]">{info.user_name}</strong>. Copy the link below to share manually.</>
            : <>Share this one-time link with <strong className="text-[color:var(--atech-text)]">{info.user_name}</strong>. The link expires in 24 hours.</>}
      </p>
      <div className="space-y-3">
        <CopyRow label="Email" value={info.user_email} copied={copied === "email"} onCopy={() => copy(info.user_email, "email")} />
        {info.justCreated ? (
          <CopyRow label="Temporary password" value={info.temp_password} copied={copied === "pw"} onCopy={() => copy(info.temp_password, "pw")} mono />
        ) : (
          <CopyRow label={info.justResent ? "Activation link" : "Reset link"} value={info.reset_url} copied={copied === "url"} onCopy={() => copy(info.reset_url, "url")} mono />
        )}
      </div>
      <div className="flex justify-end mt-6">
        <button onClick={onClose} className="btn-copper text-sm" data-testid="users-share-close">Done</button>
      </div>
    </Modal>
  );
}

function CopyRow({ label, value, copied, onCopy, mono }) {
  return (
    <div className="border border-[color:var(--atech-border)] rounded-[2px] px-4 py-3 flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--atech-text-muted)] mb-1">{label}</div>
        <div className={`text-sm text-[color:var(--atech-text)] break-all ${mono ? "font-mono" : ""}`}>{value}</div>
      </div>
      <button
        onClick={onCopy}
        className="shrink-0 text-xs flex items-center gap-1.5 px-3 py-2 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:border-[color:var(--atech-copper)]"
      >
        {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
      </button>
    </div>
  );
}

/* ───────────── Account tab (self-service) ─────────────────────────────── */

export function AccountTab() {
  const { user, refresh: refreshUser } = useAuth();
  const [profile, setProfile] = useState({ name: user?.name || "", email: user?.email || "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pBusy, setPBusy] = useState(false); const [pOk, setPOk] = useState(false); const [pErr, setPErr] = useState("");
  const [wBusy, setWBusy] = useState(false); const [wOk, setWOk] = useState(false); const [wErr, setWErr] = useState("");

  useEffect(() => { setProfile({ name: user?.name || "", email: user?.email || "" }); }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setPBusy(true); setPErr(""); setPOk(false);
    try {
      await api.patch("/auth/me", profile);
      await refreshUser?.();
      setPOk(true); setTimeout(() => setPOk(false), 2500);
    } catch (e2) { setPErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setPBusy(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setWBusy(true); setWErr(""); setWOk(false);
    if (pwd.new_password !== pwd.confirm) { setWErr("New password and confirmation don't match."); setWBusy(false); return; }
    if (pwd.new_password.length < 8) { setWErr("New password must be at least 8 characters."); setWBusy(false); return; }
    try {
      await api.post("/auth/change-password", { current_password: pwd.current_password, new_password: pwd.new_password });
      setPwd({ current_password: "", new_password: "", confirm: "" });
      setWOk(true); setTimeout(() => setWOk(false), 2500);
    } catch (e2) { setWErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setWBusy(false); }
  };

  return (
    <div data-testid="account-tab" className="space-y-6">
      <form onSubmit={saveProfile} className="card-surface p-8 space-y-5" data-testid="account-profile-form">
        <div>
          <div className="label text-[color:var(--atech-copper)] mb-1">Account</div>
          <h2 className="font-display text-2xl text-[color:var(--atech-text)]">Your profile</h2>
          <p className="text-sm text-[color:var(--atech-text-dim)] mt-1.5">Update the name and email shown to clients in activity logs and notifications.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Full name">
            <input required value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={inputCls} data-testid="account-name" />
          </Field>
          <Field label="Email">
            <input required type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className={inputCls} data-testid="account-email" />
          </Field>
        </div>
        {pErr && <ErrorBlock text={pErr} />}
        <div className="flex items-center gap-3 pt-1">
          <button type="submit" disabled={pBusy} className="btn-copper text-sm" data-testid="account-save-profile">
            {pBusy ? "Saving…" : "Save changes"}
          </button>
          {pOk && (
            <span className="text-xs text-[color:var(--st-complete-fg)] flex items-center gap-1"><Check size={12} /> Saved</span>
          )}
        </div>
      </form>

      <form onSubmit={changePassword} className="card-surface p-8 space-y-5" data-testid="account-password-form">
        <div>
          <h2 className="font-display text-2xl text-[color:var(--atech-text)]">Change password</h2>
          <p className="text-sm text-[color:var(--atech-text-dim)] mt-1.5">Use a strong password — minimum 8 characters.</p>
        </div>
        <Field label="Current password">
          <input required type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className={inputCls} data-testid="account-current-password" autoComplete="current-password" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="New password">
            <input required type="password" minLength={8} value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className={inputCls} data-testid="account-new-password" autoComplete="new-password" />
          </Field>
          <Field label="Confirm new password">
            <input required type="password" minLength={8} value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className={inputCls} data-testid="account-confirm-password" autoComplete="new-password" />
          </Field>
        </div>
        {wErr && <ErrorBlock text={wErr} />}
        <div className="flex items-center gap-3 pt-1">
          <button type="submit" disabled={wBusy} className="btn-copper text-sm" data-testid="account-save-password">
            {wBusy ? "Updating…" : "Update password"}
          </button>
          {wOk && (
            <span className="text-xs text-[color:var(--st-complete-fg)] flex items-center gap-1"><Check size={12} /> Password updated</span>
          )}
        </div>
      </form>
    </div>
  );
}

/* ───────────── Shared helpers ─────────────────────────────────────────── */

function ErrorBlock({ text }) {
  return (
    <div className="text-xs text-[color:var(--st-on-hold-fg)] border border-[color:var(--st-on-hold-bd)] bg-[color:var(--st-on-hold-bg)] rounded-[2px] px-3 py-2 flex items-start gap-2">
      <X size={12} className="mt-0.5 shrink-0" />
      <span>{text}</span>
    </div>
  );
}

function Modal({ title, children, onClose, testId }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={testId}
    >
      <div className="card-surface w-full max-w-lg p-8 fade-up max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl text-[color:var(--atech-text)]">{title}</h3>
          <button onClick={onClose} className="text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)]" data-testid="modal-close">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}


/* ───────────── Client users tab — master list across all client orgs ───── */

export function ClientUsersTab() {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const [resetInfo, setResetInfo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: us }, { data: cs }] = await Promise.all([
        api.get("/users", { params: { role: "client" } }),
        api.get("/clients"),
      ]);
      setUsers(us);
      setClients(Object.fromEntries(cs.map((c) => [c.id, c])));
    } catch {
      setUsers([]); setClients({});
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = users.filter((u) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (u.name || "").toLowerCase().includes(q) ||
           (u.email || "").toLowerCase().includes(q) ||
           (clients[u.client_id]?.name || "").toLowerCase().includes(q);
  });

  const onDelete = async (u) => {
    if (!confirm(`Delete ${u.name} (${u.email})?\n\nThis frees the email address so it can be re-invited, and removes the user from any projects they were assigned to.`)) return;
    setBusy(true);
    try {
      await api.delete(`/users/${u.id}`);
      await load();
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const onResendInvite = async (u) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${u.id}/resend-invite`);
      // Map resend-invite response fields to the shape ShareResetLinkModal expects
      setResetInfo({
        user_name: u.name,
        user_email: u.email,
        reset_url: data.activation_url,
        justResent: true,
        emailSent: data?.email?.status === "sent",
      });
      await load();
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="client-users-tab">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="label text-[color:var(--atech-copper)] mb-1">External access</div>
          <h2 className="font-display text-2xl text-[color:var(--atech-text)]">Client users</h2>
          <p className="text-sm text-[color:var(--atech-text-dim)] mt-2 max-w-xl">
            Every portal user across all client organisations, including pending invites.
            Delete an orphan to free up the email address so it can be re-invited.
          </p>
        </div>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search by name, email, or client…"
          className={`${inputCls} max-w-xs`}
          data-testid="client-users-filter"
        />
      </div>

      <div className="card-surface overflow-hidden" data-testid="client-users-table">
        {loading ? (
          <div className="p-6 label text-[color:var(--atech-text-muted)]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <div className="text-sm text-[color:var(--atech-text)]">
              {users.length === 0 ? "No client users yet." : "No users match your search."}
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--atech-surface-elevated)] text-[10px] uppercase tracking-[0.18em] text-[color:var(--atech-text-muted)]">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Name</th>
                <th className="text-left px-5 py-3 font-medium">Email</th>
                <th className="text-left px-5 py-3 font-medium">Client</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="text-left px-5 py-3 font-medium">Last sign-in</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--atech-border)]">
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`client-user-row-${u.id}`}>
                  <td className="px-5 py-3.5 text-[color:var(--atech-text)]">
                    {u.name}
                    {(u.permission === "edit" || u.client_role === "admin") && (
                      <span className="inline-flex items-center gap-1 ml-2 text-[9px] uppercase tracking-[0.18em] text-[color:var(--atech-copper)]">
                        <Shield size={9} /> {u.permission === "edit" ? "Edit" : "Admin"}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-[color:var(--atech-text-dim)] font-mono text-xs break-all">{u.email}</td>
                  <td className="px-5 py-3.5 text-[color:var(--atech-text-dim)] text-xs">
                    {clients[u.client_id]?.name || <span className="text-[color:var(--atech-text-muted)]">— orphan —</span>}
                  </td>
                  <td className="px-5 py-3.5">
                    <StatusChip status={u.status} />
                  </td>
                  <td className="px-5 py-3.5 text-[color:var(--atech-text-dim)] text-xs">
                    {u.last_login_at
                      ? new Date(u.last_login_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                      : "Never"}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {u.status !== "active" && (
                        <button
                          onClick={() => onResendInvite(u)}
                          disabled={busy}
                          title="Generate a fresh invite link and email it"
                          className="text-xs flex items-center gap-1.5 px-2.5 py-1.5 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:border-[color:var(--atech-copper)] disabled:opacity-50"
                          data-testid={`client-user-resend-${u.id}`}
                        >
                          <Mail size={11} /> Resend invite
                        </button>
                      )}
                      <button
                        onClick={() => onDelete(u)}
                        disabled={busy}
                        className="text-xs flex items-center gap-1.5 text-[color:var(--atech-text-dim)] hover:text-[#F87171] disabled:opacity-50"
                        data-testid={`client-user-delete-${u.id}`}
                      >
                        <Trash2 size={11} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {resetInfo && (
        <ShareResetLinkModal info={resetInfo} onClose={() => setResetInfo(null)} />
      )}
    </div>
  );
}
